<script lang="ts">
  /* AN ACTIVECAPTAIN POINT OF INTEREST, opened.
   *
   * ═══ WHY THIS IS SHARED AND THE BOAT'S SHEET IS NOT ═══
   *
   * The same test `TideSheet` passes: nothing below asks a question that needs a
   * vessel. An anchorage's reviews are the same reviews whether or not there is
   * a boat looking at them.
   *
   * ═══ NO {@html}, AND THAT IS THE POINT ═══
   *
   * The boat's ObjectSheet renders a sanitized HTML description, because
   * crows-nest builds one: a SignalK note carries a `description` string, and a
   * generic client can only show text. It needs `sanitize-html.ts` and the
   * dash's only `{@html}` to do that safely.
   *
   * This host's feed keeps the same data STRUCTURED all the way here — see
   * `services/layer-cache/src/feeds/activecaptain.ts` — so there is no markup to
   * trust, nothing to sanitize, and no way for a third party's review text to
   * become a third party's script. A row is a label and a value, and a value is
   * always rendered as text.
   *
   * ═══ THE DISCLAIMER IS NOT DECORATION ═══
   *
   * A depth in a review is a depth somebody remembers, at a state of tide they
   * did not write down, from a boat whose draft you do not know. The sheet has
   * to say so, in the same words the boat uses, because it looks exactly as
   * authoritative as the chart it is drawn on top of.
   */

  interface Section {
    id: string;
    title: string;
    items: { label: string; value: string | number; kind: string }[];
  }

  interface Detail {
    id: string;
    name: string;
    sections: Section[];
  }

  interface Props {
    /** The picked note's name, shown while the detail is still loading — so
     *  the sheet says WHICH place immediately rather than only "Loading". */
    name: string;
    detail: Detail | null;
    loading?: boolean;
  }
  const { name, detail, loading = false }: Props = $props();

  /* Five glyphs and the number. The stars are scannable and the number is the
   * one that survives a squint — "4/5 from 43" and "4/5 from 1" are different
   * claims, which is why the count is never dropped. */
  const starText = (v: number | string | undefined): string => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '';
    const full = Math.round(v);
    return `${'★'.repeat(Math.max(0, Math.min(5, full)))}${'☆'.repeat(Math.max(0, 5 - full))} ${v.toFixed(1)}/5`;
  };
</script>

<div class="note">
  <div class="hdr">{name}</div>

  {#if loading}
    <p class="sub">Loading ActiveCaptain…</p>
  {:else if detail && detail.sections.length}
    {#each detail.sections as sec, si (si)}
      <div class="sec">
        <div class="lbl">{sec.title.toUpperCase()}</div>
        <!-- Keyed by INDEX, not label. The API serves the same label more than
             once within a section, and a duplicate key makes Svelte throw
             each_key_duplicate — which aborts the render and leaves the sheet
             stuck on "Loading ActiveCaptain…" forever. Measured on a live
             marina record, on the boat, before this host existed. -->
        {#each sec.items as it, i (i)}
          <div class="item">
            {#if it.kind === 'rating'}
              <span class="stars">{starText(it.value)}</span>
            {:else if it.kind === 'count'}
              <span>{it.label}: <b>{it.value}</b></span>
            {:else if it.kind === 'note'}
              <span class="free">{it.value}</span>
            {:else}
              <span><span class="sub">{it.label}: </span>{it.value}</span>
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  {:else}
    <p class="sub">No ActiveCaptain details for this one.</p>
  {/if}

  <p class="sub disclaimer">
    From ActiveCaptain — someone else's opinion of this water, not a chart.
    Verify anything that matters against a real source.
  </p>
</div>

<style>
  /* Every colour is `var(--token, fallback)`, per the package's own rule: this
     has to look right in the dash's design system, in apps/web's four tokens,
     and in a host with neither. */
  .note {
    font: 12px/1.45 var(--mono, ui-monospace, monospace);
    color: var(--ink, #1d2b36);
  }
  .hdr {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .lbl {
    font-size: 10px;
    letter-spacing: 0.08em;
    opacity: 0.6;
    margin-bottom: 2px;
  }
  .sec {
    margin-bottom: 8px;
  }
  .item {
    margin: 1px 0;
  }
  .sub {
    opacity: 0.7;
  }
  .stars {
    letter-spacing: 0.05em;
  }
  /* A review is prose, not a field. Given room to wrap rather than squeezed
     onto the label/value grid the rest of the rows use. */
  .free {
    display: block;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .disclaimer {
    margin: 8px 0 0;
    border-top: 1px solid var(--line, #d5dee5);
    padding-top: 6px;
  }
</style>
